<template lang="pug">
  .gl-settings.gl-settings-groups
    // nav
    gl-nav

    // page
    .gl-settings__page.gl-layout--medium-width(layout="row")

      // sidenav
      gl-settings-sidenav

      // content
      .gl-settings__page__content(flex)

        // head
        .gl-settings__head
          .gl-settings__head__context App Settings
          .gl-settings__head__tsb(layout="row" layout-align="start space-between")
            .gl-settings__head__title Groups
            .gl-settings__head__tsb__search(flex)
              font-awesome-icon(icon="search")
              .gl-settings__head__tsb__search__input
                b-form-input(
                  v-model.trim="query"
                  v-gl-focus="true"
                  @input="bindGroups"
                  required
                  type="text"
                  placeholder='Search groups by name')
            .gl-settings__head__tsb__search__btn
              gl-btn(:options="{ label: 'Add group', type: 'button', style: 'brand-settings' }" @click.native="showAddGroupModal")

        // segment: all groups
        .gl-layout__segment(layout="column" layout-gt-md="row")
          .gl-layout__segment__meta
            .gl-layout__segment__title All Groups
            .gl-layout__segment__description Manage the users, dashboards, and settings for each of your groups.
          .gl-layout__segment__content(flex)

            // loading
            gl-loading-card(:options="{ message: 'Loading groups' }" v-if="isLoading.getGroups")

            // 404
            gl-404(:options="{ message: 'No groups found' }" v-if="isLoading.getGroups == false && groups.filtered.length == 0")

            // groups
            .gl-settings-groups__group(:class="{ 'inactive': !group.isActive, 'last': index === (groups.filtered.length - 1) }" v-for="(group, index) in groups.filtered" v-if="!isLoading.getGroups")
              // head
              .gl-settings-groups__group__head(@click="group.isOpen = !group.isOpen" layout="row")
                .gl-settings-groups__group__head__logo
                  gl-group-logo(:group-id="group.id")
                .gl-settings-groups__group__head__meta
                  .gl-settings-groups__group__head__name {{ group.name }}
                  .gl-settings-groups__group__head__secondary
                    span {{ group.users.length }} team member{{ group.users.length === 1 ? '' : 's' }}
                    span.pr-6.pl-6 &middot;
                    span.pr-6 {{ group.dashboards.all.length }} dashboard{{ group.dashboards.all.length === 1 ? '' : 's' }}
                    span(v-if="group.dashboards.inactive.length") ({{ group.dashboards.inactive.length }} inactive)

              // collapse wrapper
              b-collapse(:id="'group-content-'+group.id" v-model="group.isOpen")

                // tabs
                b-tabs.gl-settings-groups__group__tabs(v-model="group.currentTab")

                  // tab: team members
                  b-tab(title="Team Members" active)

                    // wrapper
                    .gl-settings-groups__group__tabs__wrap(v-if="group.isOpen")

                      // alerts
                      gl-alert(:options="{ errorList: group.errorList }" @clear-error-list="group.errorList = []")

                      // search
                      gl-autocomplete(
                        v-if="group.isActive"
                        :list="activeUsers"
                        :disable-pending="true"
                        :options="{ placeholder: 'Add user to ' + group.name, title: 'Select a user' }"
                        @item-clicked="(user) => { showAddUserModal({ group, user }) }")

                      // users
                      .gl-grid__wrapper(v-if="group.users.length")
                        .gl-grid__wrapper__row(v-for="user in group.users" v-bind:class="{ 'inactive': user.status === 'Inactive', 'pending': user.status === 'Pending' }" layout="row" layout-align="space-between center")
                          .gl-grid__wrapper__row__img
                            img(:src="user.email | gravatar")
                          .gl-grid__wrapper__row__name(flex)
                            span {{ user.name }}
                            span.gl-grid__wrapper__row__name__label(v-if="user.status === 'Inactive'") Inactive
                            span.gl-grid__wrapper__row__name__label(v-if="user.status === 'Pending'") Pending
                          .gl-grid__wrapper__row__action(@click="showRemoveUserModal({ group, user })") Remove from this group

                      // no users
                      .gl-grid__404(v-if="!group.users.length") No users found

                  // tab: dashboards
                  b-tab(title="Dashboards" @click="focus({ css: '#input--search-dashboards-' + group.id })")

                    // wrapper
                    .gl-settings-groups__group__tabs__wrap(v-if="group.isOpen")

                      // search
                      .gl-grid__search(layout="row")
                        font-awesome-icon(icon="search")
                        b-form-input.gl-grid__search__input(
                          v-model.trim="group.dashboardQuery"
                          @input="bindGroupDashboards(group)"
                          :id="'input--search-dashboards-' + group.id"
                          flex
                          type="text"
                          placeholder='Search for a dashboard')
                        gl-btn(
                          v-if="group.isActive"
                          @click.native="showAddEditDashboardModal({ type: 'add', group })"
                          :options="{ label: 'Add dashboard', type: 'button', style: 'smoke' }")

                      // dashboards
                      .gl-grid__wrapper(v-if="group.dashboards.filtered.length")
                        .gl-grid__wrapper__row(v-for="dashboard in group.dashboards.filtered"  :class="{ 'inactive': !dashboard.isActive }" layout="row" layout-align="space-between center")
                          .gl-grid__wrapper__row__name(flex)
                            span {{ dashboard.name }}
                            span.gl-grid__wrapper__row__name__label(v-if="!dashboard.isActive") Inactive
                          .gl-grid__wrapper__row__action.blue(@click="showAddEditDashboardModal({ type: 'edit', group, dashboard })") Edit

                      // no dashboards
                      .gl-grid__404(v-if="!group.dashboards.filtered.length") No dashboards found

                  // tab: group settings
                  b-tab.gl-settings-groups__group-settings(v-if="group.isActive" title="Group Settings" @click="focus({ css: '#input--name-' + group.id })")

                    // form: group settings
                    b-form(v-if="group.isOpen" @submit.prevent="saveGroupSettings(group)" novalidate)
                      .gl-settings-groups__group-settings__form(v-if="group.form")
                        // alerts
                        gl-alert(:options="{ errorList: group.errorList }" @clear-error-list="group.errorList = []")

                        // Name
                        b-form-group(v-if="group.form.name" :state="group.form.name.state" label="Name" label-for="input--name")
                          b-form-input(
                            v-model.trim="group.form.name.val"
                            :state="group.form.name.state"
                            :id="'input--name-' + group.id"
                            type="text")
                          .invalid-feedback(v-if="group.form.name.invalidFeedback") {{ group.form.name.invalidFeedback }}

                        // Domain
                        b-form-group(:state="group.form.domain.state" label="Domain" label-for="domain")
                          b-form-input(
                            :state="group.form.domain.state"
                            v-model.trim="group.form.domain.val"
                            type="text"
                            placeholder="example.com")
                          .invalid-feedback(v-if="group.form.domain.invalidFeedback") {{ group.form.domain.invalidFeedback }}

                        // Upload Logo
                        div(layout="row" layout-align="space-between center")
                          .gl-settings-groups__group-settings__img(v-if="group.form.base64.val")
                            img(:src="group.form.base64.val")
                          .gl-settings-groups__group-settings__img(v-if="!group.form.base64.val")
                            gl-group-logo(:domain="group.form.domain.val")
                          div(flex)
                            .gl-settings-groups__group-settings__label Logo
                            .gl-settings-groups__group-settings__description The domain specified above is used to automatically generate a logo when available. To override it, upload your own logo file here.
                          div(layout="row")
                            gl-btn.mr-12(@click.native="(base64) => bindBase64({ base64: null, group })" :options="{ label: 'Reset', type: 'button', style: 'smoke' }" v-if="group.form.base64.val")
                            gl-img-upload(@base64="(base64) => bindBase64({ base64, group })")

                        // Hide image
                        b-form-checkbox.gl-settings-groups__group-settings__checkbox(:id="'group-image-hide--' + group.id" v-model="group.form.hideImage.val" value="true" unchecked-value="false")
                          span.pr-4 Hide
                          b.pr-4 logo
                          span in header?

                      // Footer
                      .gl-settings-groups__group-settings__footer(layout="row" layout-align="end")
                        gl-btn(:options="{ label: 'Save settings', type: 'submit', style: 'brand-settings', isLoading: group.isLoading }")

                div(v-if="group.currentTab === 2 || !group.isActive")
                  // Deactivate Group
                  .gl-settings-groups__group-settings__toggle-wrap(v-if="group.isActive" layout="row" layout-align="space-between center")
                    div(flex)
                      .gl-settings-groups__group-settings__label Deactivate Group
                      .gl-settings-groups__group-settings__description(flex) If you'd like to remove this group from your account, you can do so by clicking the deactivate button on the right.
                    gl-btn(:options="{ label: 'Deactivate', type: 'button', style: 'danger-outline' }" @click.native="showToggleGroupModal({ group })")

                  // Activate Group
                  .gl-settings-groups__group-settings__toggle-wrap(v-if="!group.isActive" layout="row" layout-align="space-between center")
                    div(flex)
                      .gl-settings-groups__group-settings__label Activate Group
                      .gl-settings-groups__group-settings__description(flex) If you'd like to activate this group, you can do so by clicking the activate button on the right.
                    gl-btn(:options="{ label: 'Activate', type: 'button', style: 'success-outline' }" @click.native="showToggleGroupModal({ group })")

            // inactive toggle
            .gl-layout__segment__content__toggle(v-if="groups.inactive.length" @click="showInactiveGroups = !showInactiveGroups; bindGroups();")
              span {{ showInactiveGroups ? 'Hide': 'Show' }} inactive groups ({{ groups.inactive.length }} inactive)

    // modal: addGroup
    b-modal(:hide-footer="true" title="Add Group" ref="addGroup" size="md")
      // form
      b-form(novalidate @submit.prevent="saveAddGroup")
        // alerts
        gl-alert(:options="{ errorList: addGroup.errorList }" @clear-error-list="addGroup.errorList = []")

        // Group Name
        b-form-group(:state="addGroup.form.name.state" label="Group Name" label-for="input--group-name" flex)
          b-form-input(
            :state="addGroup.form.name.state"
            v-model.trim="addGroup.form.name.val"
            v-gl-focus="true"
            required
            id="input--group-name"
            type="text")
          .invalid-feedback(v-if="addGroup.form.name.invalidFeedback") {{ addGroup.form.name.invalidFeedback }}

        // Domain
        b-form-group(:state="addGroup.form.domain.state" label="Domain" label-for="domain" flex)
          b-form-input(
            :state="addGroup.form.domain.state"
            v-model.trim="addGroup.form.domain.val"
            required
            id="domain"
            type="text")
          .invalid-feedback(v-if="addGroup.form.domain.invalidFeedback") {{ addGroup.form.domain.invalidFeedback }}

        // Status
        b-form-group(:state="addGroup.form.status.state" label="Status" label-for="status")
          b-form-select(:state="addGroup.form.status.state" :options="addGroup.STATUSES" v-model="addGroup.form.status.val")
          .invalid-feedback(v-if="addGroup.form.status.invalidFeedback") {{ addGroup.form.status.invalidFeedback }}

        // footer
        .modal-footer(layout="row")
          gl-btn.mr-12(:options="{ label: 'Cancel', type: 'button', style: 'outline' }" @click.native="hideModal('addGroup')")
          gl-btn(:options="{ label: 'Add group', type: 'submit', style: 'brand-settings', isLoading: isLoading.addGroup }")

    // modal: addEditDashboard
    b-modal(:title="(addEditDashboard.type === 'add') ? 'Add dashboard': 'Edit dashboard'" @hidden="getDashboards" :hide-footer="true" ref="addEditDashboard" size="lg")
      // loading
      gl-loading-card(:options="{ message: 'Loading dashboard', border: 'none' }" v-if="isLoading.getDashboard")

      // form
      b-form(v-if="!isLoading.getDashboard" @submit.prevent="saveAddEditDashboard" novalidate)
        // alerts
        gl-alert(:options="{ errorList: addEditDashboard.errorList }" @clear-error-list="addEditDashboard.errorList = []")

        // heading
        .gl-layout__caps-head GENERAL

        // type
        b-form-group(:state="addEditDashboard.form.type.state" label="Type" label-for="select--type" flex)
          b-form-select(
            :state="addEditDashboard.form.type.state"
            :options="dashboardTypes"
            :disabled="addEditDashboard.type === 'edit'"
            v-model="addEditDashboard.form.type.val"
            required
            id="select--type")
          .invalid-feedback(v-if="addEditDashboard.form.type.invalidFeedback") {{ addEditDashboard.form.type.invalidFeedback }}

        // form: mode
        div(v-if="addEditDashboard.form.type.val === 'mode'")

          .gl-settings-groups__h-input-wrap(layout="row" layout-align="space-between")

            // groupId
            b-form-group(:state="addEditDashboard.form.groupId.state" label="Group" label-for="select--group" flex)
              b-form-select(
                :state="addEditDashboard.form.groupId.state"
                :options="addEditDashboard.GROUPS"
                v-model.trim="addEditDashboard.form.groupId.val"
                required
                id="select--group")
              .invalid-feedback(v-if="addEditDashboard.form.groupId.invalidFeedback") {{ addEditDashboard.form.groupId.invalidFeedback }}

            // modeReport
            b-form-group(:state="addEditDashboard.form.modeReportId.state" label="Mode Report" label-for="select--mode-report" flex)
              b-form-select(
                @input="bindModeReport({ modeReportId: addEditDashboard.form.modeReportId.val, focusName: true })"
                :state="addEditDashboard.form.modeReportId.state"
                :options="modeDashboards"
                v-model="addEditDashboard.form.modeReportId.val"
                required
                id="select--mode-report")
              .invalid-feedback(v-if="addEditDashboard.form.modeReportId.invalidFeedback") {{ addEditDashboard.form.modeReportId.invalidFeedback }}

          .gl-settings-groups__h-input-wrap(layout="row" layout-align="space-between")

            // dashboardName
            b-form-group(:state="addEditDashboard.form.dashboardName.state" label="Name" label-for="input--dash-name" flex)
              b-form-input(
                :state="addEditDashboard.form.dashboardName.state"
                v-model.trim="addEditDashboard.form.dashboardName.val"
                required
                id="input--dash-name"
                type="text")
              .invalid-feedback(v-if="addEditDashboard.form.dashboardName.invalidFeedback") {{ addEditDashboard.form.dashboardName.invalidFeedback }}

            // status
            b-form-group(:state="addEditDashboard.form.status.state" label="Status" label-for="input--status" flex)
              b-form-select(
                :state="addEditDashboard.form.status.state"
                :options="addEditDashboard.STATUSES"
                v-model.trim="addEditDashboard.form.status.val"
                required
                id="input--status")
              .invalid-feedback(v-if="addEditDashboard.form.status.invalidFeedback") {{ addEditDashboard.form.status.invalidFeedback }}

          // description
          b-form-group(:state="addEditDashboard.form.description.state" label="Description" label-for="textarea--description" flex)
            b-form-textarea(
              :state="addEditDashboard.form.description.state"
              :rows="3"
              :max-rows="6"
              v-model="addEditDashboard.form.description.val"
              required
              id="textarea--description")
            .invalid-feedback(v-if="addEditDashboard.form.description.invalidFeedback") {{ addEditDashboard.form.description.invalidFeedback }}

          // divider
          .gl-layout__divider

          // heading
          .gl-layout__caps-head PARAMETERS

          // 404 parameters
          .alert.alert-info(v-if="!addEditDashboard.form.formFields.val.length") No parameters found.

          // parameters
          .gl-settings-groups__h-input-wrap(v-for="(item, i) in addEditDashboard.form.formFields.val" :key="item.id" layout="row" layout-align="space-between start")

            // name
            b-form-group(label="Name" :label-for="item.id + '--name'" flex)
              b-form-input(
                v-model.trim="item.name.val"
                :state="item.name.state"
                :id="item.id + '--name'"
                disabled
                type="text")
              .invalid-feedback(v-if="item.name.invalidFeedback") {{ item.name.invalidFeedback }}

            // value
            b-form-group(v-if="item.formField" label="Value*" :label-for="item.id + '--value'" flex)
              // text / date
              b-form-input(
                v-if="item.formField.type === 'text' || item.formField.type === 'date'"
                v-model.trim="item.value.val"
                :state="item.value.state"
                :id="item.id + '--value'"
                :type="item.formField.type")

              // select
              div(v-if="item.formField.type === 'select'")
                multiselect(
                  :state="item.value.state"
                  :options="item.formField.$options"
                  :close-on-select="true"
                  :multiple="false"
                  :clear-on-select="true"
                  open-direction="bottom"
                  label="text"
                  track-by="value"
                  v-model="item.value.val"
                  placeholder="Please select an option"
                  required)

              // multiselect
              div(v-if="item.formField.type === 'multiselect'")
                multiselect(
                  :state="item.value.state"
                  :options="item.formField.$options"
                  :close-on-select="false"
                  :multiple="true"
                  :clear-on-select="true"
                  :group-select="true"
                  group-values="vs"
                  group-label="all"
                  open-direction="bottom"
                  label="text"
                  track-by="value"
                  v-model="item.value.val"
                  placeholder="Please select options"
                  select-group-label="Press enter to select all"
                  deselect-group-label="Press enter to deselect all"
                  required)

                  template(slot="selection" slot-scope="{ values, search, isOpen }")
                    span(v-if="values.length && !isOpen") {{ values.length }} options selected

                .invalid-feedback(v-if="item.formField.invalidFeedback") {{ item.formField.invalidFeedback }}

            // display
            b-form-checkbox.gl-settings-groups__group-settings__checkbox(:id="'group-param-display--' + item.name.val" v-model="item.formField.display" value="true" unchecked-value="false") Display in Report?

        // form: periscope
        div(v-if="addEditDashboard.form.type.val === 'periscope'")

          // Group
          b-form-group(:state="addEditDashboard.form.groupId.state" label="Group" label-for="select--group" flex)
            b-form-select(
              :state="addEditDashboard.form.groupId.state"
              :options="addEditDashboard.GROUPS"
              v-model.trim="addEditDashboard.form.groupId.val"
              required
              id="select--group")
            .invalid-feedback(v-if="addEditDashboard.form.groupId.invalidFeedback") {{ addEditDashboard.form.groupId.invalidFeedback }}

          .gl-settings-groups__h-input-wrap(layout="row" layout-align="space-between")
            // Dashboard ID
            b-form-group(:state="addEditDashboard.form.dashboardId.state" label="Periscope Dashboard ID" label-for="input--dash-id" flex)
              b-form-input(
                :state="addEditDashboard.form.dashboardId.state"
                v-model.trim="addEditDashboard.form.dashboardId.val"
                required
                id="input--dash-id"
                type="text")
              .invalid-feedback(v-if="addEditDashboard.form.dashboardId.invalidFeedback") {{ addEditDashboard.form.dashboardId.invalidFeedback }}

            // Dashboard Name
            b-form-group(:state="addEditDashboard.form.dashboardName.state" label="Dashboard Name" label-for="dashboardName" flex)
              b-form-input(
                :state="addEditDashboard.form.dashboardName.state"
                v-model.trim="addEditDashboard.form.dashboardName.val"
                required
                id="dashboardName"
                type="text")
              .invalid-feedback(v-if="addEditDashboard.form.dashboardName.invalidFeedback") {{ addEditDashboard.form.dashboardName.invalidFeedback }}

            // Status
            b-form-group(:state="addEditDashboard.form.status.state" label="Status" label-for="status" flex)
              b-form-select(
                :state="addEditDashboard.form.status.state"
                :options="addEditDashboard.STATUSES"
                v-model="addEditDashboard.form.status.val"
                required
                id="status")
              .invalid-feedback(v-if="addEditDashboard.form.status.invalidFeedback") {{ addEditDashboard.form.status.invalidFeedback }}

          .gl-settings-groups__h-input-wrap(layout="row" layout-align="space-between")

            // Chart Borders
            b-form-group(:state="addEditDashboard.form.chartBorder.state" label="Chart Borders" label-for="select--chart-borders" flex)
              b-form-select(
                :state="addEditDashboard.form.chartBorder.state"
                :options="addEditDashboard.BORDERS"
                v-model="addEditDashboard.form.chartBorder.val"
                required
                id="select--chart-borders")
              .invalid-feedback(v-if="addEditDashboard.form.chartBorder.invalidFeedback") {{ addEditDashboard.form.chartBorder.invalidFeedback }}

            // Refresh Rate (Seconds)
            b-form-group(:state="addEditDashboard.form.refreshRate.state" label="Refresh Rate (Seconds)" label-for="input--refresh-rate" flex)
              b-form-input(
                :state="addEditDashboard.form.refreshRate.state"
                v-model.trim="addEditDashboard.form.refreshRate.val"
                required
                id="input--refresh-rate"
                type="text")
              .invalid-feedback(v-if="addEditDashboard.form.refreshRate.invalidFeedback") {{ addEditDashboard.form.refreshRate.invalidFeedback }}

          // divider
          .gl-layout__divider

          // heading
          .gl-layout__caps-head EMBED SETTINGS

          .gl-settings-groups__h-input-wrap(:class="{ 'short': addEditDashboard.form.dateRange.val !== 'custom' }" layout="row" layout-align="start")
            // Default Data Aggregation
            b-form-group(:state="addEditDashboard.form.aggregation.state" label="Aggregation" label-for="aggregation" flex)
              b-form-select(
                :state="addEditDashboard.form.aggregation.state"
                :options="addEditDashboard.AGGREGATION"
                v-model.trim="addEditDashboard.form.aggregation.val"
                required
                id="aggregation")
              .invalid-feedback(v-if="addEditDashboard.form.aggregation.invalidFeedback") {{ addEditDashboard.form.aggregation.invalidFeedback }}

            // Aggregation Visibility
            b-form-group(:state="addEditDashboard.form.aggregationVisibility.state" label="Visible?" label-for="aggregationVisibility" flex)
              b-form-select(
                :state="addEditDashboard.form.aggregationVisibility.state"
                :options="addEditDashboard.VISIBILITY"
                v-model.trim="addEditDashboard.form.aggregationVisibility.val"
                required
                id="aggregationVisibility")
              .invalid-feedback(v-if="addEditDashboard.form.aggregationVisibility.invalidFeedback") {{ addEditDashboard.form.aggregationVisibility.invalidFeedback }}

          .gl-settings-groups__h-input-wrap(:class="{ 'short': addEditDashboard.form.dateRange.val !== 'custom' }" layout="row" layout-align="space-between start")
            // Default Date Range
            b-form-group(:state="addEditDashboard.form.dateRange.state" flex label="Date Range" label-for="dateRange")
              b-form-select(
                :state="addEditDashboard.form.dateRange.state"
                :options="addEditDashboard.DATE_RANGES"
                v-model.trim="addEditDashboard.form.dateRange.val"
                required
                id="dateRange")
              .invalid-feedback(v-if="addEditDashboard.form.dateRange.invalidFeedback") {{ addEditDashboard.form.dateRange.invalidFeedback }}

            // Start Date
            b-form-group(:state="addEditDashboard.form.sd.state" v-if="addEditDashboard.form.dateRange.val === 'custom'" label="Start Date" label-for="sd" flex)
              datepicker(v-model="addEditDashboard.form.sd.val" v-bind:class="{ 'is-invalid': addEditDashboard.form.sd.state === 'invalid' }" :state="addEditDashboard.form.sd.state" :open-date="addEditDashboard.form.sd.val" :bootstrap-styling="true")
              .invalid-feedback(v-if="addEditDashboard.form.sd.invalidFeedback") {{ addEditDashboard.form.sd.invalidFeedback }}

            // End Date
            b-form-group(:state="addEditDashboard.form.ed.state" v-if="addEditDashboard.form.dateRange.val === 'custom'" label="End Date" label-for="ed"  flex)
              datepicker(v-model="addEditDashboard.form.ed.val" v-bind:class="{ 'is-invalid': addEditDashboard.form.ed.state === 'invalid' }" :state="addEditDashboard.form.ed.state" :open-date="addEditDashboard.form.ed.val" :bootstrap-styling="true")
              .invalid-feedback(v-if="addEditDashboard.form.ed.invalidFeedback") {{ addEditDashboard.form.ed.invalidFeedback }}

            // Date Range Visibility
            b-form-group(:state="addEditDashboard.form.dateRangeVisibility.state" label="Visible?" label-for="dateRangeVisibility" flex)
              b-form-select(
                :state="addEditDashboard.form.dateRangeVisibility.state"
                :options="addEditDashboard.VISIBILITY"
                v-model.trim="addEditDashboard.form.dateRangeVisibility.val"
                required
                id="dateRangeVisibility")
              .invalid-feedback(v-if="addEditDashboard.form.dateRangeVisibility.invalidFeedback") {{ addEditDashboard.form.dateRangeVisibility.invalidFeedback }}

          // divider
          .gl-layout__divider

          // heading
          .gl-layout__caps-head FILTERS

          // filters
          .gl-settings-groups__h-input-wrap(v-for="(item, i) in addEditDashboard.form.filters.val" :key="item.id" layout="row" layout-align="space-between start")
            // name
            b-form-group(:state="item.name.state" label="Name*" :label-for="'filter-name-' + i" flex="20")
              b-form-input(
                :state="item.name.state"
                :id="'filter-name-' + i"
                v-model.trim="item.name.val"
                required
                type="text")
              .invalid-feedback(v-if="item.name.invalidFeedback") {{ item.name.invalidFeedback }}

            // value
            b-form-group(:state="item.value.state" label="Value" :label-for="'filter-value-' + i" flex="20")
              b-form-input(
                :state="item.value.state"
                :id="'filter-value-' + i"
                v-model.trim="item.value.val"
                type="text")
              .invalid-feedback(v-if="item.value.invalidFeedback") {{ item.value.invalidFeedback }}

            // group
            b-form-group(:state="item.group.state" label="Group" :label-for="'filter-group-' + i" flex="20")
              b-form-input(
                :state="item.group.state"
                :id="'filter-group-' + i"
                v-model.trim="item.group.val"
                type="text")
              .invalid-feedback(v-if="item.group.invalidFeedback") {{ item.group.invalidFeedback }}

            // visibility
            b-form-group(:state="item.visibility.state" :label-for="'filter-visibility-' + i" label="Visible?*" flex="20")
              b-form-select(
                :state="item.visibility.state"
                :id="'filter-visibility-' + i"
                :options="addEditDashboard.VISIBILITY"
                v-model.trim="item.visibility.val"
                required)
              .invalid-feedback(v-if="item.visibility.invalidFeedback") {{ item.visibility.invalidFeedback }}

            // remove filter
            .gl-settings-groups__remove-filter
              .gl-settings-groups__remove-filter__ninja
              b-tooltip(:target="'remove-btn' + i" title="Remove filter" placement="topleft")
              gl-btn(:id="'remove-btn' + i" :options="{ fa: 'times-circle', type: 'button', style: 'smoke' }" @click.native="removeFilter(i)")

          // add filter button
          gl-btn(:options="{ label: 'Add a filter', type: 'button', style: 'smoke' }" @click.native="addFilter()")

        // footer
        .modal-footer(v-if="addEditDashboard.form.type.val")
          div(layout="row" layout-align="end center")
            b-form-checkbox(id="addAnother" v-model="addEditDashboard.addAnother" value="true" unchecked-value="false")
              span.ml-8 Add another like this one
            gl-btn.ml-12(:options="{ label: (addEditDashboard.type === 'add') ? 'Add dashboard': 'Save changes', type: 'submit', style: 'brand-settings', isLoading: isLoading.addEditDashboard }")

    // modal: activate / deactivate group
    b-modal(:title="toggleGroupModal.group && toggleGroupModal.group.isActive ? 'Deactivate Group' : 'Activate Group'" ref="toggleGroup" size="md")

      // message
      b-alert.mb-0(v-if="toggleGroupModal.group" show variant="warning")
        span(v-if="toggleGroupModal.group.isActive")
          span.pr-4 Would you like to deactivate group
          b {{ toggleGroupModal.group.name }}
          span ?
        span(v-if="!toggleGroupModal.group.isActive")
          span.pr-4 Would you like to activate
          b {{ toggleGroupModal.group.name }}
          span ?

      // footer
      div(v-if="toggleGroupModal.group" slot="modal-footer" layout="row")
        gl-btn.mr-12(:options="{ label: 'Cancel', type: 'button', style: 'outline' }" @click.native="hideModal('toggleGroup')")
        gl-btn(@click.native="toggleGroup()" :options="{ label: toggleGroupModal.group.isActive ? 'Deactivate' : 'Activate', type: 'submit', style: toggleGroupModal.group.isActive ? 'danger-outline' : 'success-outline', isLoading: isLoading.toggleGroup }")

    // modal: activate / deactivate user
    b-modal(title="Remove User" ref="removeUser" size="md")

      // message
      b-alert.mb-0(v-if="removeUserModal.group && removeUserModal.user" show variant="warning")
        span.pr-4 Remove
        b.pr-4 {{ removeUserModal.user.name }}
        span.pr-4 from group
        b {{ removeUserModal.group.name }}
        span ?

      // footer
      div(v-if="removeUserModal.group" slot="modal-footer" layout="row")
        gl-btn.mr-12(:options="{ label: 'Cancel', type: 'button', style: 'outline' }" @click.native="hideModal('removeUser')")
        gl-btn(@click.native="removeUser()" :options="{ label: 'Remove user', type: 'button', style: 'danger-outline', isLoading: isLoading.removeUser }")

    // modal: addUser
    b-modal(title="Add User" ref="addUser" size="md")
      // message
      b-alert.mb-0(v-if="addUserModal.user && addUserModal.group" show variant="warning")
        span.pr-4 Add
        b.pr-4 {{ addUserModal.user.name }}
        span.pr-4 to group
        b {{ addUserModal.group.name }}
        span ?

      // footer
      div(slot="modal-footer" v-if="addUserModal.group" layout="row" layout-align="end center")
        gl-btn.mr-12(@click.native="hideModal('addUser')" :options="{ label: 'Cancel', type: 'button', style: 'outline' }")
        gl-btn(@click.native="addUser()" :options="{ label: 'Add User', type: 'button', style: 'brand-settings', isLoading: addUserModal.group.isLoading }")

</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import { mapGetters } from 'vuex'

export default {

  // data
  data () {
    return {
      query: '',
      groups: {
        filtered: [],
        all: [],
        active: [],
        inactive: [],
        visible: []
      },
      showInactiveGroups: false,
      allUsers: [],
      activeUsers: [],
      addGroup: {
        form: {
          name: { val: '', state: null, invalidFeedback: '' },
          domain: { val: '', state: null, invalidFeedback: '' },
          status: { val: '', state: null, invalidFeedback: '' }
        },
        errorList: [],
        STATUSES: [
          { text: 'Select', value: '', disabled: true },
          { text: 'Active', value: 'active' },
          { text: 'Inactive', value: 'inactive' }
        ]
      },
      addUserModal: {
        user: null,
        group: null
      },
      removeUserModal: {
        user: null,
        group: null
      },
      toggleGroupModal: {
        group: null
      },
      addEditDashboard: {
        type: 'add',
        group: null,
        dashboard: null,
        addAnother: false,
        modeReport: null,
        form: {
          type: { val: null, state: null, invalidFeedback: '' },
          modeReportId: { val: null, state: null, invalidFeedback: '' },
          description: { val: null, state: null, invalidFeedback: '' },
          id: { val: '', state: null, invalidFeedback: '' },
          groupId: { val: '', state: null, invalidFeedback: '' },
          originalGroupId: { val: '', state: null, invalidFeedback: '' },
          dashboardId: { val: '', state: null, invalidFeedback: '' },
          dashboardName: { val: '', state: null, invalidFeedback: '' },
          refreshRate: { val: '', state: null, invalidFeedback: '' },
          chartBorder: { val: 'off', state: null, invalidFeedback: '' },
          status: { val: '', state: null, invalidFeedback: '' },
          groupDashboardId: { val: '', state: null, invalidFeedback: '' },
          aggregation: { val: '', state: null, invalidFeedback: '' },
          aggregationVisibility: { val: '', state: null, invalidFeedback: '' },
          dateRange: { val: '', state: null, invalidFeedback: '' },
          dateRangeVisibility: { val: '', state: null, invalidFeedback: '' },
          filters: { val: [], state: null, invalidFeedback: '' },
          formFields: { val: [], state: null, invalidFeedback: '' },
          sd: { val: new Date(), state: null, invalidFeedback: '' },
          ed: { val: new Date(), state: null, invalidFeedback: '' }
        },
        errorList: [],
        GROUPS: [],
        STATUSES: [
          { text: 'Select', value: '', disabled: true },
          { text: 'Active', value: 'active' },
          { text: 'Inactive', value: 'inactive' }
        ],
        AGGREGATION: [
          { text: 'Select', value: '', disabled: true },
          { text: 'Hourly', value: 'hourly' },
          { text: 'Daily', value: 'daily' },
          { text: 'Weekly', value: 'weekly' },
          { text: 'Monthly', value: 'monthly' },
          { text: 'Quarterly', value: 'quarterly' },
          { text: 'Yearly', value: 'yearly' }
        ],
        BORDERS: [
          { text: 'Select', value: '', disabled: true },
          { text: 'Visible', value: 'on' },
          { text: 'Hidden', value: 'off' }
        ],
        VISIBILITY: [
          { text: 'Select', value: '', disabled: true },
          { text: 'Visible', value: 'visible' },
          { text: 'Hidden', value: 'hidden' }
        ],
        DATE_RANGES: [
          { text: 'Select', value: '', disabled: true },
          { text: '365 days', value: 365 },
          { text: '180 days', value: 180 },
          { text: '90 days', value: 90 },
          { text: '60 days', value: 60 },
          { text: '30 days', value: 30 },
          { text: '14 days', value: 14 },
          { text: '7 days', value: 7 },
          { text: '3 days', value: 3 },
          { text: '1 day', value: 1 },
          { text: 'Custom', value: 'custom' }
        ]
      },
      isLoading: {
        getUsers: true,
        getGroups: true,
        getDashboards: true,
        removeUser: false,
        addGroup: false,
        toggleUser: false,
        toggleGroup: false,
        getDashboard: false,
        saveGroupSettings: false,
        addEditDashboard: false
      }
    }
  },

  // computed
  computed: {
    ...mapGetters(['config', 'dashboards']),
    modeDashboards () {
      return [{ text: 'Select', value: '', disabled: true }].concat(
        _.map( // Build object
          _.filter( // Filter to only Mode Reports
            _.uniqBy( // Get unique reports by Mode token
              this.dashboards.all,
              ({ token }) => token
            ),
            ({ reportType }) => reportType === 'mode'
          ),
          (dash) => ({ value: dash.reportId, text: dash.name, og: dash })
        )
      )
      // Leaving this here incase the above update blows things up
      // return [{ text: 'Select', value: '', disabled: true }].concat(_.map(_.filter(this.dashboards.all, (dash) => { return dash.reportType === 'mode' && dash.groups.length === 0 }), (dash) => {
      //   return { value: dash.reportId, text: dash.name, og: dash }
      // }))
    },
    dashboardTypes () {
      return [{ text: 'Select', value: '', disabled: true }].concat(_.map(this.dashboards.types, (type) => {
        return { value: type, text: type.charAt(0).toUpperCase() + type.slice(1).toLowerCase() }
      }))
    }
  },

  // on mount
  mounted () {
    // get fresh dashboards
    this.getDashboards()

    // get fresh groups
    this.getGroups('init')

    // get fresh users
    this.getUsers()
  },

  // watch
  watch: {
    'dashboards.all' () {
      // update groups / group dashboards
      this.bindGroups()
    }
  },

  // methods
  methods: {
    getDashboards () {
      // get all dashboards
      return this.$glAuth.getAllDashboards()
        .catch(e => console.log(e))
        .finally(() => { this.isLoading.getDashboards = false })
    },
    getGroups (src) {
      // get fresh groups
      return this.$glAuth.getAllGroups()
        .then(r => {
          // bind all groups sorted by name
          this.groups.all = _.sortBy(r, 'name')

          // bind groups
          this.bindGroups(src)
        })
        .catch(e => console.log(e))
        .finally(() => { this.isLoading.getGroups = false })
    },
    getUsers () {
      // get fresh users
      return this.$glAuth.getUsers()
        .then(r => {
          // if results -> bind allUsers
          if (_.isObject(r)) {
            this.allUsers = r
            this.activeUsers = _.filter(this.allUsers, { status: 'Active' })
          }

          // bind pretty groups
          this.bindGroups()
        })
        .catch(e => console.log(e))
        .finally(() => { this.isLoading.getUsers = false })
    },
    bindGroups (src) {
      // if first bind -> add groups to addEditDashboard.GROUPS
      if (src === 'init') {
        // start select w/ disabled prompt
        this.addEditDashboard.GROUPS.push({ value: '', text: 'Select', disabled: true })

        // add formatted select groups
        Array.prototype.push.apply(this.addEditDashboard.GROUPS, _.map(this.groups.all, (group) => {
          return { text: group.name, value: group.id }
        }))
      }

      // add meta data for each group
      this.groups.all = _.map(this.groups.all, (group) => {
        // bind dashboardQuery
        if (_.isUndefined(group.dashboardQuery)) this.$set(group, 'dashboardQuery', '')

        // bind isOpen
        if (_.isUndefined(group.isOpen)) this.$set(group, 'isOpen', false)

        // bind currentTab
        if (_.isUndefined(group.currentTab)) this.$set(group, 'currentTab', 1)

        // bind errorList
        if (_.isUndefined(group.errorList)) this.$set(group, 'errorList', [])

        // bind isLoading
        if (_.isUndefined(group.isLoading)) this.$set(group, 'isLoading', false)

        // bind group.form
        if (_.isUndefined(group.form)) {
          // init group config
          let G_CONFIG = {}

          // if group config defined -> set group config
          if (_.isObject(this.config) && _.isObject(this.config['gid_' + group.id])) {
            G_CONFIG = this.config['gid_' + group.id]
          }

          // bind form
          this.$set(group, 'form', {
            name: { val: group.name, state: null, invalidFeedback: '' },
            domain: { val: G_CONFIG.domain, state: null, invalidFeedback: '' },
            base64: { val: G_CONFIG.image, state: null, invalidFeedback: '' },
            hideImage: { val: G_CONFIG.hideImage, state: null, invalidFeedback: '' }
          })
        }

        // bind dashboards
        this.bindGroupDashboards(group)

        // bind group.users
        this.$set(group, 'users', _.filter(this.allUsers, (user) => { return _.find(user.groups, { id: group.id }) }))

        return group
      })

      // bind active groups
      this.groups.active = _.filter(this.groups.all, { isActive: true })

      // bind inactive groups
      this.groups.inactive = _.filter(this.groups.all, (g) => { return (_.isObject(g) && !g.isActive) })

      // set visible groups based on if inactive users
      let visibleGroups = (this.showInactiveGroups) ? this.groups.active.concat(this.groups.inactive) : this.groups.active

      // set filteredGroups: filter visible groups by name
      this.groups.filtered = _.filter(visibleGroups, (group) => {
        return _.includes(group.name.toLowerCase(), this.query)
      })
    },
    bindGroupDashboards (group) {
      // init groupDashboards
      let groupDashboards = {
        all: [],
        inactive: [],
        filtered: []
      }

      // set all groupDashboards
      groupDashboards.all = _.filter(this.dashboards.all, (dashboard) => {
        return _.find(dashboard.groups, { groupId: group.id })
      })

      // set inactive groupDashboards
      groupDashboards.inactive = _.filter(groupDashboards.all, { isActive: false })

      // set filtered groupDashboards
      groupDashboards.filtered = _.filter(groupDashboards.all, (dashboard) => {
        let query = group.dashboardQuery ? group.dashboardQuery.toLowerCase() : ''
        return _.includes(dashboard.name.toLowerCase(), query)
      })

      // bind group.dashboards
      this.$set(group, 'dashboards', groupDashboards)
    },
    showToggleGroupModal ({ group }) {
      // set group
      this.toggleGroupModal.group = group

      // open modal
      this.$refs.toggleGroup.show()
    },
    saveGroupSettings (group) {
      // clear previous errors
      group.errorList = []

      // clear previous feedback
      this.$glForm.clearFeedback(group.form)

      // if form is valid
      if (this.validateGroupSettings(group)) {
        // start loading
        group.isLoading = true

        // build update request
        const UPDATE_REQUEST = {
          id: group.id,
          name: group.form.name.val
        }

        // build upsert request
        // undefined attrs don't go up in request (desirable)
        const UPSERT_REQUEST = {
          ['gid_' + group.id]: {
            domain: group.form.domain.val || undefined,
            image: group.form.base64.val || undefined,
            hideImage: group.form.hideImage.val || undefined
          }
        }

        // init promises
        let promises = [
          this.$glHttp.post('group/update', UPDATE_REQUEST),
          this.$glAuth.upsertConfig(UPSERT_REQUEST)
        ]

        // init keys we'd like to delete
        let deleteKeys = []

        // if no domain -> delete domain from config
        if (!group.form.domain.val) deleteKeys.push({ groupId: group.id, prop: 'domain' })

        // if no base64 -> delete image from config
        if (!group.form.base64.val) deleteKeys.push({ groupId: group.id, prop: 'image' })

        // if deleteKeys requested -> delete those keys
        if (deleteKeys.length) promises.push(this.$glAuth.deleteConfig(deleteKeys))

        // update group data
        return Promise.all(promises)
          .then(() => {
            // show success notification
            this.$toasted.show(`Group: ${group.form.name.val} settings saved`, {
              theme: 'primary',
              type: 'success',
              position: 'top-center',
              duration: 5000
            })

            // update group name w/ form name
            group.name = group.form.name.val

            // get fresh groups
            this.getGroups()
          })
          .catch((e) => {
            // push errors
            group.errorList.push(this.$glForm.parseError(e))
          })
          .finally(() => {
            // stop loading
            group.isLoading = false
          })
      } else {
        // focus first error
        this.$glForm.focusFirstError({ el: this.$el })
      }
    },
    validateGroupSettings (group) {
      // if no name -> invalid
      if (!group.form.name.val) {
        group.form.name.state = 'invalid'
        group.form.name.invalidFeedback = 'A name is required'
      }

      // return true if no fields are invalid
      return this.$glForm.hasNoInvalid(group.form)
    },
    focus (options) {
      // focus options
      this.$glDom.focus(options)
    },
    showAddGroupModal () {
      // open modal
      this.$refs.addGroup.show()

      // focus first input
      this.focus({ css: '#input--group-name' })
    },
    showAddEditDashboardModal (x) {
      // set type
      console.log('X IN SHOWADDEDIT: ', x)
      this.addEditDashboard.type = x.type

      // reset addAnother
      this.addEditDashboard.addAnother = false

      // reset form
      this.$glForm.reset(this.addEditDashboard.form)

      // default chartBorder
      this.addEditDashboard.form.chartBorder.val = 'off'

      // default refreshRate
      this.addEditDashboard.form.refreshRate.val = 3600

      // clear previous feedback
      this.$glForm.clearFeedback(this.addEditDashboard.form)

      // if a group is provided
      if (_.isObject(x.group)) {
        // default groupId
        this.addEditDashboard.form.groupId.val = x.group.id

        // set originalGroupId
        this.addEditDashboard.form.originalGroupId.val = x.group.id
      }

      // if a dashboard is provied
      if (_.isObject(x.dashboard)) {
        // bind dashboard type
        this.addEditDashboard.form.type.val = x.dashboard.reportType

        // if edit -> get dash
        if (this.addEditDashboard.type === 'edit') {
          // start loading
          this.isLoading.getDashboard = true

          // get dashboard
          this.$glAuth.getDashboard({ id: x.dashboard.id, type: x.dashboard.reportType })
            .then(r => {
              // set dashboard response
              const DASH_RESPONSE = r.results

              // bind id
              this.addEditDashboard.form.id.val = DASH_RESPONSE.id

              // bind dashboardName
              this.addEditDashboard.form.dashboardName.val = DASH_RESPONSE.name

              // bind status
              this.addEditDashboard.form.status.val = DASH_RESPONSE.isActive ? 'active' : 'inactive'

              // if mode -> bind mode
              if (this.addEditDashboard.form.type.val === 'mode') {
                this.bindModeReport({ modeReport: DASH_RESPONSE })
              } else if (this.addEditDashboard.form.type.val === 'periscope') {
                // bind dashboardId
                this.addEditDashboard.form.dashboardId.val = DASH_RESPONSE.config.dashboard

                // bind aggregation
                this.addEditDashboard.form.aggregation.val = DASH_RESPONSE.config.aggregation

                // bind chartBorder
                this.addEditDashboard.form.chartBorder.val = DASH_RESPONSE.config.border === 'on' ? 'on' : 'off'

                // bind refreshRate
                this.addEditDashboard.form.refreshRate.val = DASH_RESPONSE.config.data_ts

                // bind groupDashboardId
                this.addEditDashboard.form.groupDashboardId.val = _.find(DASH_RESPONSE.groupDashes, { groupId: this.addEditDashboard.form.groupId.val })

                // bind aggregationVisibility
                this.addEditDashboard.form.aggregationVisibility.val = _.includes(DASH_RESPONSE.config.visible, 'aggregation') ? 'visible' : 'hidden'

                // if daterange -> bind daterange
                if (_.isObject(DASH_RESPONSE.config.daterange)) {
                  if (DASH_RESPONSE.config.daterange.start && DASH_RESPONSE.config.daterange.end) {
                    this.addEditDashboard.form.dateRange.val = 'custom'
                    this.addEditDashboard.form.sd.val = moment(DASH_RESPONSE.config.daterange.start, 'YYYY-MM-DD').toDate()
                    this.addEditDashboard.form.ed.val = moment(DASH_RESPONSE.config.daterange.end, 'YYYY-MM-DD').toDate()
                  } else if (DASH_RESPONSE.config.daterange.days) {
                    this.addEditDashboard.form.dateRange.val = DASH_RESPONSE.config.daterange.days
                  }
                }

                // bind dateRangeVisibility
                this.addEditDashboard.form.dateRangeVisibility.val = _.includes(DASH_RESPONSE.config.visible, 'daterange') ? 'visible' : 'hidden'

                // loop response.filters
                _.forEach(DASH_RESPONSE.config.filters, (filter) => {
                  // add filter
                  this.addFilter({
                    name: filter.name,
                    value: filter.value,
                    group: filter.group,
                    visibility: _.includes(DASH_RESPONSE.config.visible, filter.name) ? 'visible' : 'hidden'
                  })
                })

                // loop response.visible
                _.forEach(DASH_RESPONSE.config.visible, (visibleFilter) => {
                  // if visibleFilter is not in response.filters, and not daterange or aggregation
                  if (!_.includes(_.map(DASH_RESPONSE.config.filters, 'name'), visibleFilter) && visibleFilter !== 'daterange' && visibleFilter !== 'aggregation') {
                    // add visibleFilter to filter list
                    this.addFilter({
                      name: visibleFilter,
                      visibility: 'visible'
                    })
                  }
                })
              }

              // focus first input
              this.focus({ css: '#select--group' })
            })
            .finally(() => {
              this.isLoading.getDashboard = false
            })
        }
      }

      // if only 1 type -> default type to the first one
      if (this.addEditDashboard.type === 'add' && _.isArray(this.dashboardTypes) && this.dashboardTypes.length === 2) {
        this.addEditDashboard.form.type.val = this.dashboardTypes[1].value
      }

      // open modal
      this.$refs.addEditDashboard.show()

      // if multiple types -> focus type
      // else if mode -> focus mode report
      // else if periscope -> focus dashboard id
      if (this.dashboardTypes.length > 2) this.focus({ css: '#select--type' })
      else if (this.addEditDashboard.form.type.val === 'mode') this.focus({ css: '#select--mode-report' })
      else if (this.addEditDashboard.form.type.val === 'periscope') this.focus({ css: '#input--dash-id' })
    },
    saveAddGroup () {
      // clear previous errors
      this.addGroup.errorList = []

      // clear previous feedback
      this.$glForm.clearFeedback(this.addGroup.form)

      // if form is valid
      if (this.validateAddGroup()) {
        // start loading
        this.isLoading.addGroup = true

        // build request
        const REQUEST = {
          name: this.addGroup.form.name.val,
          isActive: (this.addGroup.form.status.val === 'active')
        }

        // add group
        return this.$glHttp.post('group/add', REQUEST)
          .then((group) => {
            // set config w/ group domain
            const config = {
              ['gid_' + group.results.id]: { domain: this.addGroup.form.domain.val }
            }

            // upsert congig
            return this.$glAuth.upsertConfig(config)
          })
          .then(() => {
            // populate fresh groups
            return this.getGroups()
          })
          .then(() => {
            // show success notification
            this.$toasted.show(`Group: ${this.addGroup.form.name.val} created`, {
              theme: 'primary',
              type: 'success',
              position: 'top-center',
              duration: 5000
            })
          })
          .catch(e => {
            // push errors
            this.addGroup.errorList.push(this.$glForm.parseError(e))
          })
          .finally(() => {
            // stop loading
            this.isLoading.addGroup = false
          })
      } else {
        // focus first error
        this.$glForm.focusFirstError({ location: 'modal' })
      }
    },
    validateAddGroup () {
      // if no name -> invalid
      if (!this.addGroup.form.name.val) {
        this.addGroup.form.name.state = 'invalid'
        this.addGroup.form.name.invalidFeedback = 'A group name is required'
      }
      // if no domain -> invalid
      if (!this.addGroup.form.domain.val) {
        this.addGroup.form.domain.state = 'invalid'
        this.addGroup.form.domain.invalidFeedback = 'A group domain is required'
      }
      // if no status -> invalid
      if (!this.addGroup.form.status.val) {
        this.addGroup.form.status.state = 'invalid'
        this.addGroup.form.status.invalidFeedback = 'A group status is required'
      }

      // return true if no fields are invalid
      return this.$glForm.hasNoInvalid(this.addGroup.form)
    },
    async saveAddEditDashboard () {
      // clear previous errors
      this.addEditDashboard.errorList = []

      // clear previous feedback
      this.$glForm.clearFeedback(this.addEditDashboard.form)

      // if form is valid
      if (this.validateAddEditDashboard()) {
        // start loading
        this.isLoading.addEditDashboard = true

        // init dashRequest
        let dashRequest = {
          name: this.addEditDashboard.form.dashboardName.val,
          isActive: (this.addEditDashboard.form.status.val === 'active')
        }

        // set dashRequest.id
        if (this.addEditDashboard.type === 'edit') dashRequest.id = this.addEditDashboard.form.id.val

        // periscope
        if (this.addEditDashboard.form.type.val === 'periscope') {
          // set dashRequest.periscopeDashboardId
          dashRequest.periscopeDashboardId = Number(this.addEditDashboard.form.dashboardId.val)

          // set dashRequest.config
          dashRequest.config = {
            embed: 'v2',
            filters: [],
            visible: [],
            dashboard: String(this.addEditDashboard.form.dashboardId.val),
            aggregation: this.addEditDashboard.form.aggregation.val,
            data_ts: Number(this.addEditDashboard.form.refreshRate.val),
            border: this.addEditDashboard.form.chartBorder.val
          }

          // loop filters
          _.forEach(this.addEditDashboard.form.filters.val, function (filter) {
            if (filter.name.val) {
              // if visible -> push to visible
              if (filter.visibility.val === 'visible') {
                dashRequest.config.visible.push(filter.name.val)
              }

              // if value & group -> push name + value + group to filters
              // else: value -> push name + value to filters
              if (filter.value.val && filter.group.val) dashRequest.config.filters.push({ name: filter.name.val, value: filter.value.val, group: filter.group.val })
              else if (filter.value.val) dashRequest.config.filters.push({ name: filter.name.val, value: filter.value.val })
            }
          })

          // if aggregation is visible -> push aggregation to dashRequest.config.visible
          if (this.addEditDashboard.form.aggregationVisibility.val === 'visible') dashRequest.config.visible.push('aggregation')

          // if daterange is visible -> push daterange to dashRequest.config.visible
          if (this.addEditDashboard.form.dateRangeVisibility.val === 'visible') dashRequest.config.visible.push('daterange')

          // if daterange is a number -> bind daterange number to request
          // else: daterange is custom -> bind sd/ed to request
          if (_.isNumber(this.addEditDashboard.form.dateRange.val)) {
            dashRequest.config.daterange = { days: this.addEditDashboard.form.dateRange.val }
          } else if (this.addEditDashboard.form.dateRange.val === 'custom') {
            dashRequest.config.daterange = {
              start: moment(this.addEditDashboard.form.sd.val).format('YYYY-MM-DD'),
              end: moment(this.addEditDashboard.form.ed.val).format('YYYY-MM-DD')
            }
          }
        } else if (this.addEditDashboard.form.type.val === 'mode') {
          // set description
          dashRequest.description = this.addEditDashboard.form.description.val

          // set reportId
          dashRequest.reportId = this.addEditDashboard.form.modeReportId.val

          // set token
          dashRequest.token = this.addEditDashboard.modeReport.token

          // set spaceToken
          dashRequest.spaceToken = this.addEditDashboard.modeReport.spaceToken

          // set webPreviewImage
          dashRequest.webPreviewImage = this.addEditDashboard.modeReport.webPreviewImage

          // set filters
          dashRequest.filters = this.addEditDashboard.modeReport.filters

          // set dashRequest.formFields
          dashRequest.formFields = _.map(this.addEditDashboard.form.formFields.val, (filter) => {
            const field = {
              ...filter.formField,
              value: filter.value.val,
              display: Boolean(filter.formField.display === true || filter.formField.display === 'true')
            }

            if (field.type === 'select') field.value = field.value.value
            if (field.type === 'multiselect') field.value = _.map(field.value, 'value')

            delete field.$options
            return field
          })
        }

        // build dashboard uri based on type
        // const dashUri = (this.addEditDashboard.type === 'add') ? `dashboard/${this.addEditDashboard.form.type.val}/add` : `dashboard/${this.addEditDashboard.form.type.val}/update`
        const dashUri = (this.addEditDashboard.type === 'add') ? `dashboard/${this.addEditDashboard.form.type.val}/add` : `dashboard/${this.addEditDashboard.form.type.val}/update`

        // addEdit dashboard
        return this.$glHttp.post(dashUri, dashRequest)
          .then((r) => {
            console.log('R: ', r)
            // if add -> add groupdash record
            // if originalGroupId !== currentGroupId -> update groupDashboardId
            if (this.addEditDashboard.type === 'add') {
              return this.$glHttp.post('/groupdash/add', {
                dashboardId: r.results.id,
                groupId: this.addEditDashboard.form.groupId.val,
                isDefault: false,
                reportType: this.addEditDashboard.form.type.val
              })
            } else if (this.addEditDashboard.form.groupId.val !== this.addEditDashboard.form.originalGroupId.val) {
              return this.$glHttp.post('groupdash/update', {
                id: this.addEditDashboard.form.groupDashboardId.val.id,
                dashboardId: this.addEditDashboard.form.id.val,
                groupId: this.addEditDashboard.form.groupId.val,
                isDefault: false,
                reportType: this.addEditDashboard.form.type.val
              })
            }
          })
          .then(() => {
            // build message based on type
            const MESSAGE = (this.addEditDashboard.type === 'add') ? `Dashboard: ${this.addEditDashboard.form.dashboardName.val} created` : `Dashboard: ${this.addEditDashboard.form.dashboardName.val} saved`

            // show success notification
            this.$toasted.show(MESSAGE, {
              theme: 'primary',
              type: 'success',
              position: 'top-center',
              duration: 5000
            })

            // if: addAnother
            // else: close modal
            if (this.addEditDashboard.addAnother) {
              // add copy to the name
              this.addEditDashboard.form.dashboardName.val += ' Copy'

              // if type is edit -> set to add
              if (this.addEditDashboard.type === 'edit') this.addEditDashboard.type = 'add'

              // if type is mode -> focus dash name
              // else -> focus dash id
              if (this.addEditDashboard.form.type.val === 'mode') this.focus({ css: '#input--dash-name' })
              else this.focus({ css: '#input--dash-id' })
            } else {
              this.$refs.addEditDashboard.hide()
            }
          })
          .catch(e => {
            console.log(e)
            // push errors
            this.addEditDashboard.errorList.push(this.$glForm.parseError(e))
          })
          .finally(() => {
            // stop loading
            this.isLoading.addEditDashboard = false
          })
      } else {
        // focus first error
        this.$glForm.focusFirstError({ location: 'modal' })
      }
    },
    validateAddEditDashboard () {
      // !type
      if (!this.addEditDashboard.form.type.val) {
        this.addEditDashboard.form.type.state = 'invalid'
        this.addEditDashboard.form.type.invalidFeedback = 'A type is required'
      }
      // !groupId
      if (!this.addEditDashboard.form.groupId.val) {
        this.addEditDashboard.form.groupId.state = 'invalid'
        this.addEditDashboard.form.groupId.invalidFeedback = 'A group id is required'
      }
      // !dashboardName
      if (!this.addEditDashboard.form.dashboardName.val) {
        this.addEditDashboard.form.dashboardName.state = 'invalid'
        this.addEditDashboard.form.dashboardName.invalidFeedback = 'A dashboard name is required'
      }
      // !status
      if (!this.addEditDashboard.form.status.val) {
        this.addEditDashboard.form.status.state = 'invalid'
        this.addEditDashboard.form.status.invalidFeedback = 'A status is required'
      }

      // periscope
      if (this.addEditDashboard.form.type.val === 'periscope') {
        // loop filters
        _.forEach(this.addEditDashboard.form.filters.val, (filter) => {
          // clear name state + feedback
          filter.name.state = null
          filter.name.invalidFeedback = ''

          // clear visibility state + feedback
          filter.visibility.state = null
          filter.visibility.invalidFeedback = ''
        })

        // loop filters
        _.forEach(this.addEditDashboard.form.filters.val, (filter) => {
          // !name
          if (!filter.name.val) {
            filter.name.state = 'invalid'
            filter.name.invalidFeedback = 'Name required'
            this.addEditDashboard.form.filters.state = 'invalid'
          }
          // !visibility
          if (!filter.visibility.val) {
            filter.visibility.state = 'invalid'
            filter.visibility.invalidFeedback = 'Visibility required'
            this.addEditDashboard.form.filters.state = 'invalid'
          }
        })

        // !dashboardId
        if (!this.addEditDashboard.form.dashboardId.val) {
          this.addEditDashboard.form.dashboardId.state = 'invalid'
          this.addEditDashboard.form.dashboardId.invalidFeedback = 'A dashboard id is required'
        }
        // !aggregation
        if (!this.addEditDashboard.form.aggregation.val) {
          this.addEditDashboard.form.aggregation.state = 'invalid'
          this.addEditDashboard.form.aggregation.invalidFeedback = 'An aggregation is required'
        }
        // !aggregationVisibility
        if (!this.addEditDashboard.form.aggregationVisibility.val) {
          this.addEditDashboard.form.aggregationVisibility.state = 'invalid'
          this.addEditDashboard.form.aggregationVisibility.invalidFeedback = 'An aggregation visibility is required'
        }
        // !dateRange
        if (!this.addEditDashboard.form.dateRange.val) {
          this.addEditDashboard.form.dateRange.state = 'invalid'
          this.addEditDashboard.form.dateRange.invalidFeedback = 'A date range is required'
        }
        // if dateRange is custom
        if (this.addEditDashboard.form.dateRange.val === 'custom') {
          // !sd
          if (!this.addEditDashboard.form.sd.val) {
            this.addEditDashboard.form.sd.state = 'invalid'
            this.addEditDashboard.form.sd.invalidFeedback = 'Start date required when date range is custom'
          }
          // !ed
          if (!this.addEditDashboard.form.ed.val) {
            this.addEditDashboard.form.ed.state = 'invalid'
            this.addEditDashboard.form.ed.invalidFeedback = 'End date required when date range is custom'
          }
        }
        // !dateRangeVisibility
        if (!this.addEditDashboard.form.dateRangeVisibility.val) {
          this.addEditDashboard.form.dateRangeVisibility.state = 'invalid'
          this.addEditDashboard.form.dateRangeVisibility.invalidFeedback = 'A date range visibility is required'
        }
      } else if (this.addEditDashboard.form.type.val === 'mode') {
        // loop formFields
        _.forEach(this.addEditDashboard.form.formFields.val, (formField) => {
          // clear name state + feedback
          formField.name.state = null
          formField.name.invalidFeedback = ''

          // clear value state + feedback
          formField.value.state = null
          formField.value.invalidFeedback = ''
        })

        // loop formFields
        _.forEach(this.addEditDashboard.form.formFields.val, (filter) => {
          // !name
          if (!filter.name.val) {
            filter.name.state = 'invalid'
            filter.name.invalidFeedback = 'Name required'
            this.addEditDashboard.form.formFields.state = 'invalid'
          }
          // !value
          if ((filter.formField.type === 'select' || filter.formField.type === 'text' || filter.formField.type === 'date') && !filter.value.val) {
            filter.value.state = 'invalid'
            filter.value.invalidFeedback = 'Value required'
            this.addEditDashboard.form.formFields.state = 'invalid'
          }
          // !value
          if (filter.formField.type === 'multiselect' && !_.get(filter, 'value.val.length')) {
            filter.value.state = 'invalid'
            filter.value.invalidFeedback = 'Value required'
            this.addEditDashboard.form.formFields.state = 'invalid'
          }
        })

        // !modeReportId
        if (!this.addEditDashboard.form.modeReportId.val) {
          this.addEditDashboard.form.modeReportId.state = 'invalid'
          this.addEditDashboard.form.modeReportId.invalidFeedback = 'A mode report is required'
        }
      }

      // return true if no fields are invalid
      return this.$glForm.hasNoInvalid(this.addEditDashboard.form)
    },
    addFilter (x) {
      // if no options -> default the object to avoid null pointer
      if (_.isUndefined(x)) x = {}

      // if periscope
      if (this.addEditDashboard.form.type.val === 'periscope') {
        // add empty filter
        this.addEditDashboard.form.filters.val.push({})

        // get index of filter we just pushed
        let index = this.addEditDashboard.form.filters.val.length - 1

        // overwite recently pushed filter object
        this.$set(this.addEditDashboard.form.filters.val[index], 'id', _.uniqueId('filter--'))
        this.$set(this.addEditDashboard.form.filters.val[index], 'name', { val: x.name || '', state: null, invalidFeedback: '' })
        this.$set(this.addEditDashboard.form.filters.val[index], 'value', { val: x.value || '', state: null, invalidFeedback: '' })
        this.$set(this.addEditDashboard.form.filters.val[index], 'group', { val: x.group || '', state: null, invalidFeedback: '' })
        this.$set(this.addEditDashboard.form.filters.val[index], 'visibility', { val: x.visibility || '', state: null, invalidFeedback: '' })

        // return filter
        return this.addEditDashboard.form.filters.val[index]
      } else if (this.addEditDashboard.form.type.val === 'mode') {
        // add empty formField
        this.addEditDashboard.form.formFields.val.push({})

        // get index of formField we just pushed
        let index = this.addEditDashboard.form.formFields.val.length - 1

        // init val
        let val = x.formField.value

        switch (x.formField.type) {
          case 'select':
            x.formField.$options = _.map(x.formField.options, (option) => {
              if (_.isArray(option)) return { text: option[0], value: String(option[1]) }
              else if (_.has(option, 'value')) return { text: option.text, value: String(option.value) }
            })

            val = _.find(x.formField.$options, { value: _.has(val, 'value') ? val.value : val })
            break

          case 'multiselect':
            x.formField.$options = [
              {
                all: 'Select All',
                vs: _.map(x.formField.options, (option) => {
                  if (_.isArray(option)) return { text: option[0], value: String(option[1]) }
                  else if (_.has(option, 'value')) return { text: option.text, value: String(option.value) }
                })
              }
            ]

            let newVal = []

            _.forEach(val, (v) => {
              let newValue = _.find(x.formField.$options[0].vs, { value: _.has(v, 'value') ? v.value : v })
              if (newValue) newVal.push(newValue)
            })

            val = newVal
            break
        }

        // overwite recently pushed filter object
        this.$set(this.addEditDashboard.form.formFields.val[index], 'id', _.uniqueId('form-field--'))
        this.$set(this.addEditDashboard.form.formFields.val[index], 'name', { val: x.formField.label || '', state: null, invalidFeedback: '' })
        this.$set(this.addEditDashboard.form.formFields.val[index], 'value', { val: val, state: null, invalidFeedback: '' })
        this.$set(this.addEditDashboard.form.formFields.val[index], 'formField', x.formField)

        // return formField
        return this.addEditDashboard.form.formFields.val[index]
      }
    },
    removeFilter (i) {
      // remove filter item at index
      this.addEditDashboard.form.filters.val.splice(i, 1)
    },
    bindBase64 ({ group, base64 }) {
      // set base64 in form
      this.$set(group.form.base64, 'val', base64)
    },
    hideModal (ref) {
      // hide modal @ passed in ref
      this.$refs[ref].hide()
    },
    toggleGroup () {
      // set group
      const GROUP = this.toggleGroupModal.group

      // start loading
      this.isLoading.toggleGroup = true

      // send request
      return this.$glHttp.post('group/update', { id: GROUP.id, isActive: !GROUP.isActive })
        .then(() => {
          // build successMessage based on isActive
          let successMessage = (this.toggleGroupModal.group.isActive) ? `${GROUP.form.name.val} is now inactive` : `${GROUP.form.name.val} is now active`

          // show success notification
          this.$toasted.show(successMessage, {
            theme: 'primary',
            type: 'success',
            position: 'top-center',
            duration: 5000
          })

          // toggle isActive
          this.toggleGroupModal.group.isActive = !this.toggleGroupModal.group.isActive

          // get fresh groups
          this.getGroups()

          // hide modal
          this.hideModal('toggleGroup')
        })
        // log errors
        .catch(e => {
          // show error toast
          this.$toasted.show(this.$glForm.parseError(e), {
            theme: 'primary',
            type: 'error',
            position: 'top-center',
            duration: 5000
          })
        })
        // stop loading
        .finally(() => { this.isLoading.toggleGroup = false })
    },
    showRemoveUserModal ({ group, user }) {
      // set group
      this.removeUserModal.group = group

      // set user
      this.removeUserModal.user = user

      // open modal
      this.$refs.removeUser.show()
    },
    removeUser (i) {
      // start loading
      this.isLoading.removeUser = true

      // build request
      const REQUEST = {
        removedGroups: [ { userId: this.removeUserModal.user.id, groupId: this.removeUserModal.group.id } ]
      }

      // delete usergroup
      return this.$glHttp.post('usergroup/delete', REQUEST)
        .then(() => {
          // show success toast
          this.$toasted.show(`User: ${this.removeUserModal.user.name} removed`, {
            theme: 'primary',
            type: 'success',
            position: 'top-center',
            duration: 5000
          })

          // get fresh users
          this.getUsers()
        })
        .catch(e => {
          // show error toast
          this.$toasted.show(this.$glForm.parseError(e), {
            theme: 'primary',
            type: 'error',
            position: 'top-center',
            duration: 5000
          })
        })
        .finally(() => {
          // stop loading
          this.isLoading.removeUser = false
        })
    },
    showAddUserModal ({ group, user }) {
      // set group
      this.addUserModal.group = group

      // set user
      this.addUserModal.user = user

      // open modal
      this.$refs.addUser.show()
    },
    addUser () {
      // build request
      const REQUEST = {
        addedGroups: [ { userId: this.addUserModal.user.id, groupId: this.addUserModal.group.id } ]
      }

      // start loading
      this.addUserModal.group.isLoading = true

      // add usergroup
      return this.$glHttp.post('usergroup/add', REQUEST)
        .then(() => {
          // get fresh users
          return this.getUsers()
        })
        .then(() => {
          // show success toast
          this.$toasted.show(`User: ${this.addUserModal.user.name} invited`, {
            theme: 'primary',
            type: 'success',
            position: 'top-center',
            duration: 5000
          })

          // hide modal
          this.$refs.addUser.hide()
        })
        .catch(e => {
          // show error toast
          this.$toasted.show(this.$glForm.parseError(e), {
            theme: 'primary',
            type: 'error',
            position: 'top-center',
            duration: 5000
          })
        })
        .finally(() => {
          // stop loading
          this.addUserModal.group.isLoading = false
        })
    },
    bindModeReport ({ modeReport, modeReportId, focusName }) {
      // if modeReportId
      if (modeReportId) {
        // get modeDash
        const modeDash = _.find(this.modeDashboards, { value: modeReportId })

        // bind modeReport
        modeReport = _.isObject(modeDash) ? modeDash.og : null
      }

      // if modeReport
      if (_.isObject(modeReport)) {
        // bind modeReport
        this.addEditDashboard.modeReport = modeReport

        // bind modeReportId
        this.addEditDashboard.form.modeReportId.val = modeReport.reportId

        // bind name
        this.addEditDashboard.form.dashboardName.val = modeReport.name

        // bind description
        this.addEditDashboard.form.description.val = modeReport.description

        // clear formFields
        this.addEditDashboard.form.formFields.val = []

        // loop formFields -> addFilter
        _.forEach(modeReport.formFields, (formField) => this.addFilter({ formField }))

        // if focusName requested -> focus name input
        if (focusName) this.focus({ css: '#input--dash-name' })
      }
    }
  }
}
</script>

<style lang="scss">
  @import '../styles/colors';
  @import '../styles/variables';

  .gl-settings-groups {

    .gl-layout__segment__content {
      margin-top: $gl-gutter;
    }

    &__group {
      background: $gl-white;
      border: 1px solid $gl-grey-border;
      border-radius: 4px;
      margin-bottom: 0.5rem;
      &:hover {
        box-shadow: $gl-box-shadow-light;
      }
      &.inactive {
        background-color: $gl-grey-smoke;

        .gl-settings-groups__group__head:hover {
          background: $gl-grey-smoke;
        }
      }
      &.last {
        margin-bottom: 0;
      }

      &__head {
        cursor: pointer;
        padding: $gl-gutter;
        &:hover {
          background: rgba($gl-white, 0.8);
        }

        &__logo {
          width: 40px;
          height: 40px;
          margin-right: 14px;

          img {
            max-width: 100%;
            max-height: 100%;
            width: auto;
          }
        }

        &__name, &__secondary {
          font-size: 15px;
        }

        &__name {
          font-weight: 700;
          color: $gl-grey-darker;
          margin-bottom: 6px;
        }

        &__secondary {
          color: $gl-grey;
          font-weight: 400;
        }
      }

      &__tabs {

        &__wrap {
          padding: $gl-gutter;
        }

        .nav-item {
          margin: 0 0 -1px 5px;
          &:first-child {
            margin-left: $gl-gutter;
          }

          .nav-link {
            font-size: 15px;
            padding: 0.75rem 0.75rem;
          }
        }

        .tab-content {
          padding: 0;
        }
      }
    }

    &__group-settings {

      &__img {
        width: 60px;
        height: 60px;

        img {
          max-width: 100%;
          max-height: 100%;
          width: auto;
          padding-right: 1rem;
        }
      }

      &__checkbox {
        margin-top: 40px;

        input, label {
          cursor: pointer;
        }

        input {
          margin-right: 8px;
          transform: scale(1.5);
        }

        b {
          color: $gl-grey-darker;
        }
      }

      &__form {
        padding: $gl-gutter $gl-gutter 10px $gl-gutter;
      }

      &__label {
        font-weight: 600;
        color: $gl-grey-darker;
        line-height: 1.5;
        padding-bottom: calc(0.375rem + 1px);
      }

      &__description {
        font-size: 15px;
        color: $gl-grey-dark;
        letter-spacing: 0;
        line-height: 18px;
        margin-right: $gl-gutter;
        font-weight: 400;
      }

      &__footer {
        padding: $gl-gutter;
      }

      &__toggle-wrap {
        background: $gl-grey-smoke;
        padding: $gl-gutter;
        border-top: 1px solid $gl-grey-border;
      }
    }

    &__h-input-wrap {
      margin-bottom: 1rem;
      &.short {
        max-width: 80%;
      }

      .b-form-group {
        margin-bottom: 0;
        margin-right: 1rem;
        &:last-child {
          margin-right: 0;
        }
      }
    }

    &__remove-filter {

      &__ninja {
        background: transparent;
        height: 31px;
        width: 1px;
      }

      button {
        height: 38px;
        text-align: center;
        padding: 8px 14px !important;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/views/SettingsGroups.vue